import { API_BASE_URL, USER_LOGIN_CHECK_TIMEOUT, USER_LOGIN_REFRESH_GRACE, USER_LOGIN_STALE_GRACE } from "config";
import { debounce } from "lodash";
import decode from "jwt-decode";

// Contains the redirect paths when the user is logged out
const _expirationRedirectHandlers = [];

// LocalStorage keys
const TOKEN_STORAGE_KEY = "id_token";
const LOGIN_STORAGE_KEY = "last_login_time";

export default class ServiceBase {
  /**
   * Flag capturing if the expiration debounce is already running to prevent multiple parallel executions.
   * Member is static because this class is inherited and reused.
   */
  static isDebounceRunning = false;

  /**
   * Flag capturing if the user is currently on a page that is protected from authentication expiry.
   * Member is static because this class is inherited and reused.
   */
  static isOnProtectedPage = false;

  /**
   * Flag determining if the inactivity modal should be displayed to the user.
   * Member is static because this class is inherited and reused.
   */
  static openInactivityModal = false;

  constructor(domain) {
    // API server domain
    this.domain = domain || API_BASE_URL;

    // React binding
    this.fetch = this.fetch.bind(this);

    // Check regularly to see if our session has expired.
    this.debounceExpiration = debounce(this.expirationRedirect, USER_LOGIN_CHECK_TIMEOUT);
    this.debounceExpiration();
  }

  /**
   * Gets the user token from localStorage
   * @returns {string | null} The user token of the user
   */
  getToken() {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  }

  /**
   * Sets the user token from localStorage.
   * If value is not valid, clears value instead.
   * @param {any} token The user token of the user
   */
  setToken(token) {
    if (!token) {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
    } else {
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
    }
  }

  /**
   * Removes the user token from localStorage
   */
  removeToken() {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  }

  /**
   * Gets the last login time from localStorage
   * @returns {number | null} The last login time of the user
   */
  getLastLoginTime() {
    const lastLoginTime = parseInt(localStorage.getItem(LOGIN_STORAGE_KEY));

    // If value is not present or is not a number, return as null
    if (isNaN(lastLoginTime)) {
      return null;
    }
    return lastLoginTime;
  }

  /**
   * Sets the last login time from localStorage.
   * If value is not valid, clears value instead.
   * @param {number} lastLoginTime The last login time of the user
   */
  setLastLoginTime(lastLoginTime) {
    if (isNaN(lastLoginTime)) {
      localStorage.removeItem(LOGIN_STORAGE_KEY);
    } else {
      localStorage.setItem(LOGIN_STORAGE_KEY, lastLoginTime);
    }
  }

  /**
   * Removes the last login time from localStorage
   */
  removeLastLoginTime() {
    localStorage.removeItem(LOGIN_STORAGE_KEY);
  }

  /**
   * Adds a function to be called when the login session ends.
   * @param {any} fn Function to call when the login session ends.
   */
   addLogoutHandler(fn) {
     _expirationRedirectHandlers.push(fn);
   }

  /**
   * Clears the user token and profile data
   */
  logout() {
    this.removeToken();
    this.removeLastLoginTime();
    ServiceBase.openInactivityModal = false;
    ServiceBase.isDebounceRunning = false;
    _expirationRedirectHandlers.forEach((handler) => handler());
  }

  /**
   * Helper to determine if the user token provided is expired or is still valid
   * @param {any} token User token
   * @param {number} gracePeriod Grace period to take into account when checking token (in miliseconds)
   * @returns {boolean} true if the user's token has expired, false otherwise
   */
  isTokenExpiring(token, gracePeriod=0) {
    try {
      // Get current token if not provided
      if (!token) {
        token = this.getToken();
      }

      // Decode the token
      const decoded = decode(token);

      // Check if the token is expired, accounting for the grace period
      if (decoded.exp < new Date(Date.now() + gracePeriod) / 1000) {
        return true;
      }
    } catch (err) {
      console.error("Error validating user token.", err)
    }

    return false;
  }

  /**
   * Helper to determine if the user is still logged in.
   * @param {any} token User token
   * @returns {boolean} true if the user is still logged in
   */
  loggedIn(token) {
    // Get current token if not provided
    if (!token) {
      token = this.getToken();
    }

    // Check if token is present and if it is still valid
    return !!token && !this.isTokenExpiring(token);
  }

  /**
   * Simple helper to get the username of the currently logged in user.
   * @param {any} token User token
   * @returns {string} Username of the logged in user
   */
  getUsername(token) {
    // Get current token if not provided
    if (!token) {
      token = this.getToken();
    }

    // Extract the username from the token
    return decode(token).username;
  }

  /**
   * Simple helper to refresh the login token for the user.
   * @param {any} token User token
   * @param {boolean} resetLastLoginTime Flag to force a reset of the last login time
   */
  async refreshToken(token, resetLastLoginTime=false) {
    // Get current token if not provided
    if (!token) {
      token = this.getToken();
    }

    // Try to refresh the token
    try {
      const res = await this.fetch(`${this.domain}/refresh`, {
        method: 'GET',
        headers: { Authorization: 'Bearer ' + token },
      });
      this.setToken(res.token);

      // If we're on a protected page, we reset the last login time
      if (ServiceBase.isOnProtectedPage || resetLastLoginTime) {
        this.setLastLoginTime(Date.now());
      }
    } catch (err) {
      // If we cannot refresh the token, log error as the user will eventually log out
      console.error("Unable to refresh login token.", err);
    }
  }

  /**
   * Kicks you back to the login page if your token has expired.
   * Otherwise, restarts the counter that calls this method after the configured time.
   */
  async expirationRedirect() {
    // Check the flag to ensure we aren't runing multiple times
    if (ServiceBase.isDebounceRunning) {
      return;
    }
    ServiceBase.isDebounceRunning = true;

    // Get current token
    const token = this.getToken();
    if (token === null) {
      // Already performed logout functionality.
      ServiceBase.isDebounceRunning = false;
      return;
    }

    // Handle automatically logging the user out
    if (!this.loggedIn(token)) {
      this.logout();
      return;
    }

    // Handle various grace periods
    if (this.getLastLoginTime() < Date.now() - USER_LOGIN_STALE_GRACE) {
      // If it has been a long time since user logged in, confirm user is still present or on a protected page
      if (ServiceBase.isOnProtectedPage) {
        // If the user is on a protected page, refresh the token
        await this.refreshToken(token);
      } else {
        // If the user is not on a protected page, we need to ask if they wish to remain signed in
        ServiceBase.openInactivityModal = true;
      }
    } else if (this.isTokenExpiring(token, USER_LOGIN_REFRESH_GRACE)) {
      // If close to expiration, refresh token
      await this.refreshToken(token);
    }

    // Kick off expiration poller
    ServiceBase.isDebounceRunning = false;
    this.debounceExpiration();
  }

  /**
   * Wrapper around the built-in fetch() API to include the user's authorization token.
   * @param {string} url OData route
   * @param {any[]} options Fetch config
   * @param {boolean} isJson Expected response format is in JSON or not
   * @returns {any} Fetch response
   */
  async fetch(url, options, isJson = true) {
    // performs api calls sending the required authentication headers
    const headers = {
      Accept: "application/json",
      "Content-Type": "application/json",
    };

    // Setting Authorization header
    // Authorization: Bearer xxxxxxx.xxxxxxxx.xxxxxx
    if (this.loggedIn()) {
      headers["Authorization"] = "Bearer " + this.getToken();
    }

    // Perform call
    const response = await fetch(url, {
      headers,
      ...options,
    });

    // Validate response is expected
    return await this._checkStatus(response, isJson);
  }

  /**
   * Validates the response and determines if the response is in the expected format and has the desired status code.
   * @param {any} response Raw fetch response
   * @param {boolean} isJson Expected payload format is in JSON or not
   * @returns {any} Response in expected format
   */
  async _checkStatus(response, isJson) {
    // Get desired payload from the response
    const payload = isJson
      ? await response.json()
      : response;

    // Determine if response was successful using the default
    if (response.status >= 200 && response.status < 300) {
      // Response is valid
      return payload;
    }

    // Response is invalid
    return Promise.reject(payload);
  }
}
